import {useMutation, useQuery} from '@tanstack/react-query';
import {
  ProcessPaymentLinkRequest,
  ProcessPaymentLinkResponse,
  getPaymentLinkDetails,
  processPaymentLinkCardPayment,
  processPaymentLinkPaypalPayment,
} from './api';
import {useState} from 'react';
import {handleErrorResponse} from '~/utils/axios';

export const usePaymentLinkDetails = (
  appointmentId?: string,
  paymentId?: string
) => {
  const {data, isLoading, error, refetch} = useQuery({
    queryKey: ['payment-link-details ', appointmentId, paymentId],
    queryFn: () =>
      getPaymentLinkDetails({
        appointment_id: appointmentId,
        payment_id: paymentId,
      }),
    enabled: !!(appointmentId || paymentId),
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  return {
    paymentDetails: data?.data,
    appointmentDetails: data?.data?.appointment,
    paymentInfo: data?.data?.payment,
    isLoading,
    error,
    refetch,
  };
};

export const useProcessPaymentLinkCardPayment = () => {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate: processPaymentLinkCardPaymentMutation, isPending} =
    useMutation({
      mutationKey: ['process-payment-link-card-payment'],
      mutationFn: processPaymentLinkCardPayment,
      onError: error => {
        handleErrorResponse(error, 'Card Payment');
      },
      onSettled: () => setDisableSubmit(false),
    });

  const processCardPayment = async (params: ProcessPaymentLinkRequest) => {
    setDisableSubmit(true);
    return new Promise<ProcessPaymentLinkResponse>(resolve => {
      processPaymentLinkCardPaymentMutation(params, {
        onSuccess: data => {
          resolve(data);
        },
        onError: () => {
          resolve({
            success: false,
            message: 'Card payment failed.',
          });
        },
        onSettled: () => {
          setDisableSubmit(false);
        },
      });
    });
  };

  return {
    processCardPayment,
    isProcessingPayment: isPending || disableSubmit,
  };
};

export const useProcessPaymentLinkPaypalPayment = () => {
  const [disableSubmit, setDisableSubmit] = useState(false);

  const {mutate: processPaymentLinkPaypalPaymentMutation, isPending} =
    useMutation({
      mutationKey: ['process-payment-link-paypal-payment'],
      mutationFn: processPaymentLinkPaypalPayment,
      onError: error => {
        handleErrorResponse(error, 'PayPal Payment');
      },
      onSettled: () => setDisableSubmit(false),
    });

  const processPaypalPayment = async (params: ProcessPaymentLinkRequest) => {
    setDisableSubmit(true);
    return new Promise<ProcessPaymentLinkResponse>(resolve => {
      processPaymentLinkPaypalPaymentMutation(params, {
        onSuccess: data => {
          resolve(data);
        },
        onError: () => {
          resolve({
            success: false,
            message: 'PayPal payment failed.',
          });
        },
        onSettled: () => {
          setDisableSubmit(false);
        },
      });
    });
  };

  return {
    processPaypalPayment,
    isProcessingPayment: isPending || disableSubmit,
  };
};
